/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {translate} from '@ion/core/src/locale/locale';
import Device from '@ion/core/src/services/Device';
import {isSheetTableView} from '@ion/dls/src/components/sidesheet/helpers';
import {SideSheetView} from '@ion/dls/src/types/types';
import {
  isAsstesCountLoadingSelector,
  largeFleetAssetCountSelector,
  largeFleetAssetsSelector,
  largeFleetGridCountSelector
} from '../../../../store/selectors/tracking-selectors';
import useGridFilteredItems from '../tracking/useGridFilteredItems';

/**
 * React Hook provide util function to return translated Module label/Tracking Sidesheet Grid header.
 *
 *  ```typescript
 *
 *   import {useAssetlength} from '@/routes/tracking/hooks/sidesheet/useAssetlength';
 *
 *   const label = useAssetlength(isLargeFleetMode, scDevices, sideSheetView);  // SHOWING 15 ASSETS & DEVICES
 *
 *  ```
 */
export const useAssetlength =
(
  isLargeFleetMode: boolean,
  scDevices: Device[],
  sideSheetView: SideSheetView
): string => {
  const [resultModuleLabel, setResultModuleLabel] = useState('');
  const [loading, setLoading]  = useState(false);
  const largeFleetAssetCount   = useSelector(largeFleetAssetCountSelector);
  const largeFleetGridCount    = useSelector(largeFleetGridCountSelector);
  const largeFleetAssets       = useSelector(largeFleetAssetsSelector);
  const isCountLoading         = useSelector(isAsstesCountLoadingSelector);

  const {
    gridFilteredAssets,
    gridFilteredDevices
  } = useGridFilteredItems();

  const nfmAssetsCount  = useMemo(() => (
    isLargeFleetMode ? 0 : (gridFilteredAssets.length ?? 0) + (gridFilteredDevices.length ?? 0)
  ), [gridFilteredAssets, gridFilteredDevices, isLargeFleetMode]);

  useEffect(() => {
    const delay = setTimeout(() => {
      setLoading(isCountLoading);
    }, isCountLoading ? 0: 800);

    return () => clearTimeout(delay);
  }, [isCountLoading]);

  useEffect(() => {
    if (isCountLoading || loading) {
      setResultModuleLabel('loading');
      return;
    }
    if (isLargeFleetMode) {
      const isTabelView = isSheetTableView(sideSheetView);
      setResultModuleLabel(translate('nav:sidepanel.assetsCount', {
        loaded : isTabelView ? largeFleetGridCount : largeFleetAssets?.length,
        total  : largeFleetAssetCount
      }));
    } else {
      setResultModuleLabel(translate('nav:sidepanel.showAssets', {
        assetLength : isNaN(nfmAssetsCount) ? 0 : nfmAssetsCount,
        devices     : gridFilteredDevices?.length ? translate('nav:sidepanel.showAssetsAndDevices') : null
      }));
    }
  }, [
    loading,
    isCountLoading,
    isLargeFleetMode,
    largeFleetAssetCount,
    largeFleetGridCount,
    largeFleetAssets,
    nfmAssetsCount,
    sideSheetView,
    gridFilteredDevices
  ]);

  return resultModuleLabel;
};

// private utility functions to get the asset count...
const getFilteredAssetCount = (assets: Device[], filterAssetIds: number[]): number => {
  if (filterAssetIds) {
    const idMap = {};
    filterAssetIds?.forEach((id: number) => {
      idMap[id] = true;
    });
    return assets?.filter(asset => idMap[asset?.id?.()])?.length;
  } else {
    return assets?.length;
  }
};
